import { useState, useRef, useEffect } from 'react';
import { Clock } from 'lucide-react';
import './ui-components.css';

export default function CustomDuration({ value, onChange, placeholder = "0 min" }) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value ? value.toString() : '');
    const ref = useRef(null);

    const PRESETS = [15, 30, 45, 60, 90, 120];

    useEffect(() => {
        setInputValue(value ? value.toString() : '');
    }, [value]);

    useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                if (inputValue) {
                    const parsed = parseInt(inputValue, 10);
                    if (!isNaN(parsed) && parsed !== value) {
                        onChange(parsed);
                    }
                } else if (!inputValue && value) {
                    onChange(null);
                }
            }
        };
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open, inputValue, value, onChange]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setOpen(false);
            if (inputValue) {
                const parsed = parseInt(inputValue, 10);
                if (!isNaN(parsed)) onChange(parsed);
            } else {
                onChange(null);
            }
        }
    };

    const handlePreset = (preset) => {
        onChange(preset);
        setOpen(false);
    };

    const formatDisplay = (mins) => {
        if (!mins) return '';
        if (mins < 60) return `${mins}m`;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
    };

    return (
        <div className="custom-duration" ref={ref}>
            <div 
                className={`custom-duration__trigger ${open ? 'custom-duration__trigger--open' : ''} ${!value && !open ? 'custom-duration__trigger--placeholder' : ''}`}
                onClick={() => setOpen(true)}
            >
                <div className="custom-duration__input-wrap">
                    <input
                        type="number"
                        className="custom-duration__input"
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        min="0"
                    />
                    {(value > 0 || inputValue) && <span className="custom-duration__suffix">min</span>}
                </div>
                {value > 0 && !open && (
                    <span className="custom-duration__display">{formatDisplay(value)}</span>
                )}
            </div>

            {open && (
                <div className="custom-duration__dropdown animate-fade-in-scale">
                    <div className="custom-duration__presets">
                        {PRESETS.map(p => (
                            <button
                                key={p}
                                type="button"
                                className={`custom-duration__preset ${value === p ? 'custom-duration__preset--active' : ''}`}
                                onClick={() => handlePreset(p)}
                            >
                                {p < 60 ? `${p}m` : (p === 60 ? '1h' : `${p/60}h`)}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
